import { useEffect, useState } from 'react';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import styles from './styles.module.css';
import { getDriversRequest } from '../../types/driver';
import Select from '../../components/common/Select';
import CheckBoxGroup, {
  CheckBoxGroupOnChangeProps,
  ValueObj,
} from '../../components/common/CheckBoxGroup';
import Pagenation from '../../components/common/Pagenation';
import DriversTable from './components/DriversTable';
import {
  useGetDriverLicensesQuery,
  useGetDriverSkillLevelsQuery,
  useGetDriversQuery,
} from '../../api/driverApi';
import { useGetCarQualificationsQuery } from '../../api/carApi';
import EditSideBar, {
  DriverFormDataProvider,
  DriverFormDataProviderProps,
} from './components/EditSideBar';
import PolicyRequired from '../../components/common/PolicyRequired';
import CsvInput from '../../components/CsvInput';
import CsvDownload from '../../components/CsvDownload';
import HeaderLayout from '../../components/HeaderLayout';
import SelectPageSize from '../../components/common/SelectPageSize';
import NoResultFound from '../../components/common/NoResultFound';
import { keyList, useKeyAction } from '../../services/useKeyAction';

function Drivers() {
  const initDrivers: Omit<getDriversRequest, 'page' | 'limit'> = {
    driverName: '',
    skillLevelId: null,
    driverLicenseIds: [],
    carQualificationIds: [],
  };

  // ページャーなどの情報
  const [driversData, updateDriversData] = useState<getDriversRequest>({
    ...initDrivers,
    page: 1,
    limit: 20,
  });

  const { data: drivers } = useGetDriversQuery(driversData);

  //ドライバー名
  const [driverName, setDriverName] = useState(initDrivers.driverName);
  const onChangedDriverName = (name: string) => {
    setDriverName(name);
  };

  // 習熟度
  const { data: skillLevel } = useGetDriverSkillLevelsQuery();
  const skillLevelsOptions = (skillLevel || []).map((c) => ({
    label: c.skillLevel,
    value: `${c.skillLevelId}`,
  }));
  const [skillLevelId, setSkillLevelId] = useState<number | null>(null);
  const onChangeSkillLevel = (type: string) => {
    if (type === '') {
      setSkillLevelId(null);
    }
    setSkillLevelId(Number(type));
  };

  // 免許種別
  const { data: driverLicense } = useGetDriverLicensesQuery();
  const [
    driverLicensesCheckBoxGroupValues,
    updateDriverLicensesCheckBoxGroupValues,
  ] = useState<ValueObj[]>([]);
  useEffect(() => {
    updateDriverLicensesCheckBoxGroupValues(
      (driverLicense || []).map((item) => ({
        value: `${item.driverLicenseId}`,
        label: item.driverLicense,
        checked: false,
      }))
    );
  }, [driverLicense]);
  const [driverLicenseIds, setDriverLicenseIds] = useState<number[]>([]);
  const onChangeDriverLicense = ({
    values,
    selectedValues,
  }: CheckBoxGroupOnChangeProps) => {
    updateDriverLicensesCheckBoxGroupValues(values);
    setDriverLicenseIds(selectedValues.map((v) => Number(v)));
  };

  // 資格
  const { data: carQualification } = useGetCarQualificationsQuery();
  const [
    carQualificationsCheckBoxGroupValues,
    updateCarQualificationsCheckBoxGroupValues,
  ] = useState<ValueObj[]>([]);
  useEffect(() => {
    updateCarQualificationsCheckBoxGroupValues(
      (carQualification || []).map((item) => ({
        value: `${item.carQualificationId}`,
        label: item.carQualification,
        checked: false,
      }))
    );
  }, [carQualification]);
  const [carQualificationIds, setCarQualificationIds] = useState<number[]>([]);
  const onChangecarQualification = ({
    values,
    selectedValues,
  }: CheckBoxGroupOnChangeProps) => {
    updateCarQualificationsCheckBoxGroupValues(values);
    setCarQualificationIds(selectedValues.map((v) => Number(v)));
  };

  // 条件クリア
  const onClear = () => {
    onChangedDriverName('');
    setSkillLevelId(null);
    setDriverLicenseIds([]);
    setCarQualificationIds([]);
    updateDriverLicensesCheckBoxGroupValues(
      driverLicensesCheckBoxGroupValues.map((v) => ({ ...v, checked: false }))
    );
    updateCarQualificationsCheckBoxGroupValues(
      carQualificationsCheckBoxGroupValues.map((v) => ({
        ...v,
        checked: false,
      }))
    );
    updateDriversData((prev) => ({
      ...prev,
      driverName: '',
      skillLevelId: null,
      driverLicenseIds: [],
      carQualificationIds: [],
      page: 1,
    }));
  };

  // 検索
  const onSubmit = () => {
    updateDriversData((prev) => ({
      ...prev,
      driverName,
      skillLevelId,
      driverLicenseIds,
      carQualificationIds,
      page: 1,
    }));
  };

  // ページャー変更
  const onChangePerPage = (perPage: string) => {
    updateDriversData((prev) => ({ ...prev, page: 1, limit: Number(perPage) }));
  };
  const onPage = (page: number) => {
    updateDriversData((prev) => ({
      ...prev,
      page,
    }));
  };

  const [formData, setFormData] = useState<DriverFormDataProviderProps | null>(
    null
  );

  const onConditionEnter = useKeyAction(keyList.ENTER, onSubmit);

  return (
    <HeaderLayout
      title="ドライバー情報"
      controls={
        <PolicyRequired policy={6}>
          <div className={styles.buttons}>
            <div className={styles.button}>
              <CsvDownload type={'driver'} />
            </div>
            <div className={styles.button}>
              <CsvInput type={'driver'} />
            </div>
          </div>
        </PolicyRequired>
      }
    >
      <div className={styles.mainContainer}>
        <PolicyRequired policy={7}>
          <div className={styles.conditions} onKeyDown={onConditionEnter}>
            <div className={styles.conditionInputs}>
              <div className={styles.driverName}>
                <Input
                  value={driverName}
                  onChange={(e) => onChangedDriverName(e.target.value)}
                  variants={['fit']}
                  placeholder="ドライバー名"
                ></Input>
              </div>
              <div className={styles.skillLevel}>
                <Select
                  options={skillLevelsOptions}
                  value={skillLevelId !== null ? `${skillLevelId}` : ''}
                  onChange={(e) => onChangeSkillLevel(e.target.value)}
                  variants={['fit']}
                  placeholder="習熟度"
                ></Select>
              </div>
              <div className={styles.driverLicense}>
                <CheckBoxGroup
                  values={driverLicensesCheckBoxGroupValues}
                  onChange={onChangeDriverLicense}
                  placeholder="免許種別"
                  isVertical={true}
                ></CheckBoxGroup>
              </div>
              <div className={styles.carQualification}>
                <CheckBoxGroup
                  values={carQualificationsCheckBoxGroupValues}
                  onChange={onChangecarQualification}
                  placeholder="資格"
                  isVertical={true}
                ></CheckBoxGroup>
              </div>
            </div>
            <div className={styles.conditionButtons}>
              <div className={styles.clearButton}>
                <Button variants={['contain', 'cancel']} onClick={onClear}>
                  クリア
                </Button>
              </div>
              <Button
                variants={['contain', 'secondary', 'search']}
                onClick={onSubmit}
              >
                検索
              </Button>
            </div>
          </div>
        </PolicyRequired>
        {(drivers ? drivers.drivers : []).length > 0 ? (
          <>
            <div className={styles.pageContainer}>
              <SelectPageSize
                value={`${driversData.limit}`}
                onChange={(e) => onChangePerPage(e.target.value)}
              />
              <div>
                <Pagenation
                  page={driversData.page || 1}
                  onPage={onPage}
                  perPage={driversData.limit || 0}
                  total={drivers ? drivers.total : 0}
                ></Pagenation>
              </div>
            </div>
            <div className={styles.content}>
              <DriversTable
                drivers={drivers ? drivers.drivers : []}
                openEdit={setFormData}
                sidebarClose={() => {
                  setFormData(null);
                }}
              />
            </div>
          </>
        ) : (
          <div className={styles.noResultFound}>
            <NoResultFound />
          </div>
        )}
      </div>
      <DriverFormDataProvider value={formData}>
        <EditSideBar />
      </DriverFormDataProvider>
    </HeaderLayout>
  );
}

export default Drivers;
